/**
 * Vehicles Store - Zustand
 *
 * @module core/modules/waste/store
 *
 * @description
 * Gestiona el estado de vehículos del módulo Waste.
 * Incluye posiciones en tiempo real y estado de tracking.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Point } from 'geojson';
import type { WasteVehicle, VehiclePositionUpdate } from '../types';

/* ===== TYPES ===== */

export interface VehiclePosition {
  geometry: Point;
  speed?: number;
  heading?: number;
  timestamp: string;
}

export interface VehiclesState {
  /**
   * Lista de vehículos cargados
   */
  vehicles: WasteVehicle[];

  /**
   * Posiciones en tiempo real (vehicleId -> position)
   */
  positions: Map<string, VehiclePosition>;

  /**
   * Estado de carga
   */
  isLoading: boolean;

  /**
   * Error de carga
   */
  error: string | null;

  /**
   * ID del vehículo seleccionado para tracking
   */
  trackedVehicleId: string | null;
}

export interface VehiclesActions {
  /**
   * Cargar vehículos
   */
  setVehicles: (vehicles: WasteVehicle[]) => void;

  /**
   * Agregar vehículo
   */
  addVehicle: (vehicle: WasteVehicle) => void;

  /**
   * Actualizar vehículo
   */
  updateVehicle: (id: string, updates: Partial<WasteVehicle>) => void;

  /**
   * Eliminar vehículo
   */
  removeVehicle: (id: string) => void;

  /**
   * Actualizar posición en tiempo real
   */
  updatePosition: (update: VehiclePositionUpdate) => void;

  /**
   * Actualizar múltiples posiciones
   */
  updatePositions: (updates: VehiclePositionUpdate[]) => void;

  /**
   * Seleccionar vehículo para tracking
   */
  trackVehicle: (vehicleId: string | null) => void;

  /**
   * Estado de carga
   */
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  /**
   * Limpiar store
   */
  clear: () => void;
}

export type VehiclesStore = VehiclesState & VehiclesActions;

/* ===== INITIAL STATE ===== */

const initialState: VehiclesState = {
  vehicles: [],
  positions: new Map(),
  isLoading: false,
  error: null,
  trackedVehicleId: null,
};

/* ===== STORE ===== */

export const useVehiclesStore = create<VehiclesStore>()(
  devtools(
    (set) => ({
      ...initialState,

      setVehicles: (vehicles) => {
        set({ vehicles, isLoading: false, error: null });
      },

      addVehicle: (vehicle) => {
        set((state) => ({
          vehicles: [...state.vehicles, vehicle],
        }));
      },

      updateVehicle: (id, updates) => {
        set((state) => ({
          vehicles: state.vehicles.map((v) => (v.id === id ? { ...v, ...updates } : v)),
        }));
      },

      removeVehicle: (id) => {
        set((state) => ({
          vehicles: state.vehicles.filter((v) => v.id !== id),
          positions: (() => {
            const newPositions = new Map(state.positions);
            newPositions.delete(id);
            return newPositions;
          })(),
        }));
      },

      updatePosition: (update) => {
        set((state) => {
          const newPositions = new Map(state.positions);
          newPositions.set(update.vehicleId, {
            geometry: update.position,
            speed: update.speed,
            heading: update.heading,
            timestamp: update.timestamp,
          });
          return { positions: newPositions };
        });
      },

      updatePositions: (updates) => {
        set((state) => {
          const newPositions = new Map(state.positions);
          updates.forEach((update) => {
            newPositions.set(update.vehicleId, {
              geometry: update.position,
              speed: update.speed,
              heading: update.heading,
              timestamp: update.timestamp,
            });
          });
          return { positions: newPositions };
        });
      },

      trackVehicle: (vehicleId) => {
        set({ trackedVehicleId: vehicleId });
      },

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error, isLoading: false }),

      clear: () => set(initialState),
    }),
    {
      name: 'waste-vehicles-store',
    }
  )
);

/* ===== SELECTORS ===== */

export const selectVehicles = (state: VehiclesStore): WasteVehicle[] => state.vehicles;
export const selectVehicleById = (id: string) => (state: VehiclesStore): WasteVehicle | undefined =>
  state.vehicles.find((v) => v.id === id);
export const selectPositions = (state: VehiclesStore): Map<string, VehiclePosition> => state.positions;
export const selectVehiclePosition = (id: string) => (state: VehiclesStore): VehiclePosition | undefined =>
  state.positions.get(id);
export const selectTrackedVehicleId = (state: VehiclesStore): string | null => state.trackedVehicleId;
export const selectIsLoading = (state: VehiclesStore): boolean => state.isLoading;
export const selectError = (state: VehiclesStore): string | null => state.error;
